import { create } from 'zustand';

/**
 * Initiative Store
 *
 * Manages combat initiative order for characters and mobs.
 */

export interface InitiativeEntry {
  id: string;
  name: string;
  initiative: number;
  dexMod: number; // For tiebreakers
  type: 'character' | 'mob';
  // Character-specific
  characterId?: string;
  badgeId?: string;
  badgeIcon?: string;
  // Mob-specific
  mobType?: string;
  count?: number; // For grouped mobs
  // Status
  isActive: boolean;
  hasActed: boolean;
  conditions: string[];
}

interface InitiativeState {
  // Data
  entries: InitiativeEntry[];
  currentIndex: number;
  round: number;
  isInCombat: boolean;

  // Actions
  startCombat: () => void;
  endCombat: () => void;
  nextTurn: () => void;
  previousTurn: () => void;
  setCurrentIndex: (index: number) => void;

  // Entry management
  addEntry: (entry: Omit<InitiativeEntry, 'isActive' | 'hasActed'>) => void;
  removeEntry: (id: string) => void;
  updateEntry: (id: string, updates: Partial<InitiativeEntry>) => void;
  setInitiative: (id: string, initiative: number) => void;

  // Bulk operations
  rollAllInitiative: () => void;
  resetInitiative: () => void;  // Reset rolls but keep combatants
  clearAllEntries: () => void;  // Remove all combatants
  sortByInitiative: () => void;

  // Queries
  getCurrentEntry: () => InitiativeEntry | null;
  getNextEntry: () => InitiativeEntry | null;
}

export const useInitiativeStore = create<InitiativeState>()((set, get) => ({
  entries: [],
  currentIndex: -1,
  round: 0,
  isInCombat: false,

  // =======================================================================
  // COMBAT CONTROL
  // =======================================================================

  startCombat: () => {
    const { entries } = get();
    if (entries.length === 0) return;

    // Sort by initiative (descending), then dex mod for ties
    const sorted = [...entries].sort((a, b) => {
      if (b.initiative !== a.initiative) {
        return b.initiative - a.initiative;
      }
      return b.dexMod - a.dexMod;
    });

    // Reset all entries
    const reset = sorted.map((e, i) => ({
      ...e,
      isActive: i === 0,
      hasActed: false,
    }));

    set({
      entries: reset,
      currentIndex: 0,
      round: 1,
      isInCombat: true,
    });
  },

  endCombat: () => {
    set({
      entries: [],
      currentIndex: -1,
      round: 0,
      isInCombat: false,
    });
  },

  nextTurn: () => {
    const { entries, currentIndex, round } = get();
    if (entries.length === 0) return;

    // Mark current as acted
    const updated = entries.map((e, i) => ({
      ...e,
      hasActed: i === currentIndex ? true : e.hasActed,
      isActive: false,
    }));

    // Find next index
    let nextIndex = currentIndex + 1;
    let newRound = round;

    if (nextIndex >= entries.length) {
      // New round
      nextIndex = 0;
      newRound = round + 1;
      // Reset hasActed for all
      updated.forEach((e) => {
        e.hasActed = false;
      });
    }

    // Mark next as active
    updated[nextIndex].isActive = true;

    set({
      entries: updated,
      currentIndex: nextIndex,
      round: newRound,
    });
  },

  previousTurn: () => {
    const { entries, currentIndex, round } = get();
    if (entries.length === 0 || round === 1 && currentIndex === 0) return;

    const updated = entries.map((e) => ({
      ...e,
      isActive: false,
    }));

    let prevIndex = currentIndex - 1;
    let newRound = round;

    if (prevIndex < 0) {
      prevIndex = entries.length - 1;
      newRound = Math.max(1, round - 1);
    }

    updated[prevIndex].isActive = true;
    updated[prevIndex].hasActed = false;

    set({
      entries: updated,
      currentIndex: prevIndex,
      round: newRound,
    });
  },

  setCurrentIndex: (index) => {
    const { entries } = get();
    if (index < 0 || index >= entries.length) return;

    const updated = entries.map((e, i) => ({
      ...e,
      isActive: i === index,
    }));

    set({
      entries: updated,
      currentIndex: index,
    });
  },

  // =======================================================================
  // ENTRY MANAGEMENT
  // =======================================================================

  addEntry: (entry) => {
    set((state) => {
      // Deduplicate: remove any existing entry with the same id
      const filtered = state.entries.filter(e => e.id !== entry.id);
      const newEntries = [
        ...filtered,
        { ...entry, isActive: false, hasActed: false },
      ];
      // Auto-sort by initiative (descending), then dex mod for ties
      return {
        entries: newEntries.sort((a, b) => {
          if (b.initiative !== a.initiative) {
            return b.initiative - a.initiative;
          }
          return b.dexMod - a.dexMod;
        }),
      };
    });
  },

  removeEntry: (id) => {
    set((state) => ({
      entries: state.entries.filter((e) => e.id !== id),
    }));
  },

  updateEntry: (id, updates) => {
    set((state) => ({
      entries: state.entries.map((e) =>
        e.id === id ? { ...e, ...updates } : e
      ),
    }));
  },

  setInitiative: (id, initiative) => {
    set((state) => {
      // Update the initiative value
      const updated = state.entries.map((e) =>
        e.id === id ? { ...e, initiative } : e
      );
      // Auto-sort by initiative (descending), then dex mod for ties
      const sorted = [...updated].sort((a, b) => {
        if (b.initiative !== a.initiative) {
          return b.initiative - a.initiative;
        }
        return b.dexMod - a.dexMod;
      });
      return { entries: sorted };
    });
  },

  // =======================================================================
  // BULK OPERATIONS
  // =======================================================================

  rollAllInitiative: () => {
    set((state) => ({
      entries: state.entries.map((e) => ({
        ...e,
        initiative: Math.floor(Math.random() * 20) + 1 + e.dexMod,
      })),
    }));
  },

  resetInitiative: () => {
    // Keep combatants but clear their initiative rolls and reset combat state
    set((state) => ({
      entries: state.entries.map((e) => ({
        ...e,
        initiative: 0,
      })),
      currentIndex: -1,
      round: 0,
      isInCombat: false,
    }));
  },

  clearAllEntries: () => {
    set({
      entries: [],
      currentIndex: -1,
      round: 0,
      isInCombat: false,
    });
  },

  sortByInitiative: () => {
    set((state) => ({
      entries: [...state.entries].sort((a, b) => {
        if (b.initiative !== a.initiative) {
          return b.initiative - a.initiative;
        }
        return b.dexMod - a.dexMod;
      }),
    }));
  },

  // =======================================================================
  // QUERIES
  // =======================================================================

  getCurrentEntry: () => {
    const { entries, currentIndex } = get();
    return currentIndex >= 0 && currentIndex < entries.length
      ? entries[currentIndex]
      : null;
  },

  getNextEntry: () => {
    const { entries, currentIndex } = get();
    const nextIndex = (currentIndex + 1) % entries.length;
    return entries.length > 0 ? entries[nextIndex] : null;
  },
}));
